import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Check, ShieldCheck, Flame, ArrowUpRight } from "lucide-react";
import Seo, { organizationSchema, breadcrumbSchema } from "@/lib/seo";
import PageHero from "@/components/PageHero";
import SectionHeading from "@/components/SectionHeading";
import { Reveal, StaggerGroup, fadeUp } from "@/components/motion/Reveal";
import CTASection from "@/components/CTASection";
import Approach from "@/components/sections/Approach";
import { IMAGES } from "@/data/site";
import { CORE_AREAS } from "@/data/site";
import { useSite } from "@/hooks/content";

const commitments = [
  "Practical and independent professional advice",
  "Solutions aligned with Singapore legislation and applicable standards",
  "Clear recommendations",
  "Responsive client support",
  "Competent and engaging training",
  "Continuous improvement",
  "Professional integrity and confidentiality",
];

export default function About() {
  const { site } = useSite();

  return (
    <>
      <Seo
        title="About Us"
        description="Learn about SKY Training & Consultancy — a Singapore-based Workplace Safety & Health and Fire Safety consultancy and training company built on the philosophy: Assess. Advise. Implement. Improve."
        path="/about"
        schema={organizationSchema(site)}
      />
      <PageHero
        eyebrow="About SKY"
        title="Helping Organisations Build Safer, Better-Prepared Workplaces"
        description="A Singapore-based professional consultancy and training company specialising in Workplace Safety & Health and Fire Safety."
        image={IMAGES.wshInspection}
        breadcrumb={[{ name: "Home", path: "/" }, { name: "About Us", path: "/about" }]}
      />

      {/* About */}
      <section className="section-pad">
        <div className="container-sky grid gap-12 lg:grid-cols-[1.1fr_0.9fr] lg:gap-16">
          <div>
            <SectionHeading eyebrow="Who We Are" title="About SKY" />
            <Reveal delay={0.1}>
              <div className="mt-6 space-y-5 text-base leading-relaxed text-slate-600 sm:text-lg">
                <p>
                  SKY Training &amp; Consultancy Pte Ltd is a Singapore-based professional consultancy
                  and training company specialising in Workplace Safety &amp; Health and Fire Safety. We
                  help organisations build safer, more compliant and better-prepared workplaces.
                </p>
                <p>
                  We work with organisations through professional consultancy, compliance advisory,
                  assessments, safety management systems, emergency preparedness and practical training —
                  translating obligations and good practice into solutions that work on the ground.
                </p>
                <p>
                  Our focus is practical and outcome-driven: helping clients understand risks, implement
                  appropriate controls, develop competent people and continuously improve their safety
                  performance.
                </p>
              </div>
            </Reveal>
          </div>
          <Reveal delay={0.15} className="relative">
            <div className="overflow-hidden rounded-[1.8rem] shadow-[0_24px_60px_rgba(3,56,106,0.16)]">
              <img src={IMAGES.consultantMeeting} alt="SKY consultants meeting a management team" className="aspect-[4/5] w-full object-cover" />
            </div>
          </Reveal>
        </div>
      </section>

      {/* Core areas */}
      <section className="section-pad bg-white">
        <div className="container-sky">
          <SectionHeading align="center" eyebrow="Our Expertise" title="Our Core Areas of Expertise" className="mb-14" />
          <div className="grid gap-7 md:grid-cols-2">
            {CORE_AREAS.map((area) => (
              <Reveal key={area.id} className="rounded-[1.6rem] border border-slate-100 bg-surface p-8">
                <span className="flex h-14 w-14 items-center justify-center rounded-2xl bg-brand/10 text-brand">
                  {area.id === "wsh" ? <ShieldCheck className="h-7 w-7" /> : <Flame className="h-7 w-7" />}
                </span>
                <h3 className="mt-5 font-heading text-2xl font-bold text-navy">{area.title}</h3>
                <ul className="mt-6 grid grid-cols-1 gap-3 sm:grid-cols-2">
                  {area.items.map((i) => (
                    <li key={i} className="flex items-center gap-2.5">
                      <Check className="h-4 w-4 shrink-0 text-brand" strokeWidth={3} />
                      <span className="text-sm font-medium text-navy">{i}</span>
                    </li>
                  ))}
                </ul>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Approach reused */}
      <Approach />

      {/* Practical & industry focused */}
      <section className="section-pad bg-white">
        <div className="container-sky grid items-center gap-12 lg:grid-cols-2 lg:gap-16">
          <Reveal className="order-2 overflow-hidden rounded-[1.8rem] shadow-xl lg:order-1">
            <img src={IMAGES.industrial} alt="Industrial safety inspection in Singapore" className="aspect-[4/3] w-full object-cover" />
          </Reveal>
          <div className="order-1 lg:order-2">
            <SectionHeading eyebrow="Practical & Industry-Focused" title="Different Environments Require Different Approaches" />
            <Reveal delay={0.1}>
              <p className="mt-6 text-base leading-relaxed text-slate-600 sm:text-lg">
                We recognise that a manufacturing plant, a construction site, a warehouse and a
                commercial office each face different hazards and obligations. SKY adapts its
                recommendations to the client's industry, workforce, workplace hazards, regulatory
                obligations, existing systems, business requirements and available resources — so
                advice is always relevant and workable.
              </p>
            </Reveal>
          </div>
        </div>
      </section>

      {/* Commitment */}
      <section className="section-pad">
        <div className="container-sky">
          <SectionHeading align="center" eyebrow="Our Commitment" title="What You Can Expect From SKY" className="mb-14" />
          <StaggerGroup className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {commitments.map((c, i) => (
              <motion.div key={c} variants={fadeUp()} className="flex items-start gap-4 rounded-2xl border border-slate-100 bg-white p-6">
                <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-brand font-mono-tech text-xs font-bold text-white">
                  {String(i + 1).padStart(2, "0")}
                </span>
                <span className="text-sm font-semibold leading-relaxed text-navy">{c}</span>
              </motion.div>
            ))}
          </StaggerGroup>
        </div>
      </section>

      {/* Philosophy */}
      <section className="pb-4">
        <div className="container-sky">
          <Reveal className="relative overflow-hidden rounded-[2rem] bg-gradient-to-br from-navy to-navy-deep p-10 text-center sm:p-16">
            <div className="pointer-events-none absolute inset-0 bg-blueprint-dark opacity-30" />
            <span className="tech-tag text-brand-light">Our Service Philosophy</span>
            <p className="relative mx-auto mt-5 max-w-3xl font-heading text-2xl font-bold leading-snug text-white sm:text-3xl lg:text-4xl">
              Assess. Advise. Implement. Improve.
            </p>
            <p className="relative mx-auto mt-5 max-w-2xl text-base leading-relaxed text-slate-300">
              SKY aims to help clients understand risks, implement practical controls, develop
              competent people and continuously improve safety performance.
            </p>
            <Link to="/services" className="btn-primary-sky mt-8 !bg-white !text-navy hover:!bg-brand-light">
              Explore Our Services <ArrowUpRight className="h-4 w-4" />
            </Link>
          </Reveal>
        </div>
      </section>

      <CTASection />
    </>
  );
}
